<template>
  <!--
    Shown instead of the add form when an account has hit a limit.

    Who sees it: long-standing free accounts. New signups are routed to plan
    selection before they can create anything, so this reaches people who
    have built a page over time - often for years - and are being stopped by
    us while adding to it. So it speaks to them as members: what they have
    built, with their own numbers, that it stays theirs whatever they decide,
    and why we ask them to support the site.

    "tab-full" is the exception: a paying account at the per-tab cap, which
    exists to keep pages scannable. That is never a sale; it points to a new
    tab.
  -->
  <div class="limit-popup" :class="`limit-popup--${variant}`">
    <button type="button" class="limit-popup-close" @click="close" :aria-label="$t('limitPopup.close')">
      <span class="material-icons">close</span>
    </button>

    <h2 class="limit-popup-title">{{ title }}</h2>
    <p class="limit-popup-body">{{ body }}</p>

    <p v-if="isUpsell" class="limit-popup-price">
      <strong>{{ $t('limitPopup.price.perMonth', { amount: yearlyPerMonth }) }}</strong>
      {{ $t('limitPopup.price.billedYearly') }}
      <span class="limit-popup-price-alt">
        {{ $t('limitPopup.price.orMonthly', { amount: monthly }) }} · {{ $t('limitPopup.price.refund') }}
      </span>
    </p>

    <md-button class="md-raised md-primary limit-popup-cta" @click="primary">
      {{ ctaLabel }}
    </md-button>

    <p class="limit-popup-secondary">
      <button type="button" class="limit-popup-link" @click="close">
        {{ isUpsell ? $t('limitPopup.notNow') : $t('limitPopup.close') }}
      </button>
    </p>
  </div>
</template>

<script>
import PaymentsService from '@/services/payments.service';
import EngagementService from '@/services/engagement.service';

const VARIANTS = ['tabs', 'cells', 'follow', 'public', 'tab-full'];

// Same fallbacks as Billing.vue, so a failed plans request still shows the
// prices the billing page would.
const FALLBACK_MONTHLY = 8;
const FALLBACK_YEARLY = 55;

// One request per page load however many times the popup opens.
let plansRequest = null;

export default {
  name: 'SubscriptionModal',
  props: {
    limitType: {
      type: String,
      default: 'tabs',
    },
  },
  data: () => ({
    monthly: FALLBACK_MONTHLY,
    yearly: FALLBACK_YEARLY,
  }),
  computed: {
    variant() {
      return VARIANTS.includes(this.limitType) ? this.limitType : 'tabs';
    },
    user() {
      return this.$store.getters.user || {};
    },
    isUpsell() {
      return this.variant !== 'tab-full';
    },
    // What this person has actually saved, counted from the tabs already in
    // the store (each tab arrives with its sites). Null when the tabs are not
    // loaded, in which case the copy says it without numbers.
    saved() {
      const tabs = this.$store.getters.userTabs;
      if (!Array.isArray(tabs) || !tabs.length) return null;
      return {
        tabs: tabs.length,
        sites: tabs.reduce((sum, tab) => sum + ((tab && tab.tabCells && tab.tabCells.length) || 0), 0),
      };
    },
    title() {
      switch (this.variant) {
        case 'follow':
          return this.$t('limitPopup.follow.title');
        case 'public':
          return this.$t('limitPopup.publicPage.title');
        case 'tab-full':
          return this.$t('limitPopup.tabFull.title', { count: this.user.plan_cells_number });
        default:
          return this.$t('limitPopup.member.title');
      }
    },
    body() {
      switch (this.variant) {
        case 'follow':
          return this.$t('limitPopup.follow.body');
        case 'public':
          return this.$t('limitPopup.publicPage.body');
        case 'tab-full':
          return this.$t('limitPopup.tabFull.body', { count: this.user.plan_cells_number });
        default:
          return `${this.savedSentence} ${this.$t('limitPopup.member.ask')}`;
      }
    },
    savedSentence() {
      const { saved } = this;
      if (!saved || (this.variant === 'cells' && !saved.sites)) {
        return this.$t('limitPopup.member.countsNone');
      }
      if (this.variant === 'cells') {
        return this.$t('limitPopup.member.countsSites', { sites: saved.sites });
      }
      return saved.sites
        ? this.$t('limitPopup.member.countsTabs', { tabs: saved.tabs, sites: saved.sites })
        : this.$t('limitPopup.member.countsTabsOnly', { tabs: saved.tabs });
    },
    ctaLabel() {
      switch (this.variant) {
        case 'follow':
        case 'public':
          return this.$t('limitPopup.cta');
        case 'tab-full':
          return this.$t('limitPopup.tabFull.cta');
        default:
          return this.$t('limitPopup.member.cta');
      }
    },
    yearlyPerMonth() {
      return (this.yearly / 12).toFixed(2);
    },
    // Recorded as the "upsell" surface in engagement_stats: impressions are
    // page views where the popup was shown, clicks are presses of the main
    // button.
    trackingEvent() {
      return {
        surface: 'upsell', cellId: `limit-popup:${this.variant}`, lineId: '', position: -1,
      };
    },
  },
  mounted() {
    this.loadPrices();
    this.track('impression');
  },
  methods: {
    loadPrices() {
      if (!this.isUpsell) return;
      plansRequest = plansRequest || PaymentsService.getPlans();
      plansRequest
        .then((res) => {
          const plans = (res && res.data && res.data.data && res.data.data[0]) || [];
          if (plans[1] && plans[1].amount) this.monthly = plans[1].amount;
          if (plans[2] && plans[2].amount) this.yearly = plans[2].amount;
        })
        .catch(() => {
          plansRequest = null;
        });
    },
    track(kind) {
      if (!EngagementService.enabled(this.$store.getters.user)) return;
      EngagementService[kind](this.trackingEvent);
    },
    primary() {
      this.track('click');
      if (!this.isUpsell) {
        this.$emit('new-tab');
        return;
      }
      this.$emit('close');
      this.$router.push({ name: 'billing', query: { reason: this.variant } }).catch(() => {});
    },
    close() {
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
.limit-popup {
  position: relative;
  box-sizing: border-box;
  width: 100%;
  max-width: 460px;
  margin: 0 auto;
  padding: 40px 32px 28px;
  background: #fff;
  border-top: 6px solid #FC23D9;
  text-align: center;
  font-family: 'Mulish', sans-serif;
  color: #2a2438;
}

.limit-popup--tab-full {
  border-top-color: #2b4bff;
}

.limit-popup-close {
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 6px;
  border: 0;
  background: transparent;
  color: #8a8a99;
  cursor: pointer;
}

.limit-popup-title {
  margin: 0 0 12px;
  font-size: 26px;
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: -0.3px;
}

.limit-popup-body {
  margin: 0 0 20px;
  font-size: 16px;
  line-height: 1.5;
  color: #4a4458;
}

.limit-popup-price {
  margin: 0 0 20px;
  font-size: 15px;
  line-height: 1.5;
  color: #4a4458;
}

.limit-popup-price strong {
  font-size: 20px;
  color: #2a2438;
}

.limit-popup-price-alt {
  display: block;
  font-size: 13px;
  color: #8a8a99;
}

.limit-popup-cta {
  width: 100%;
  max-width: 280px;
  height: 48px;
  margin: 0 0 12px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 700;
}

.limit-popup-secondary {
  margin: 0;
  font-size: 13px;
  color: #8a8a99;
}

.limit-popup-link {
  padding: 8px 4px;
  border: 0;
  background: transparent;
  color: #4a4458;
  font: inherit;
  text-decoration: underline;
  cursor: pointer;
}

@media (max-width: 480px) {
  .limit-popup {
    padding: 36px 20px 24px;
  }

  .limit-popup-title {
    font-size: 22px;
  }
}
</style>
